import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { RebuildLoading } from "@/components/Loading/RebuildLoading";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Validates that the URL is a legitimate Vercel preview URL for the Rebuild project.
 * This prevents open redirect attacks by only allowing redirects to our own preview deployments.
 */
function isValidPreviewUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname.startsWith("rebuild-") &&
      parsed.hostname.endsWith(".vercel.app") &&
      parsed.protocol === "https:"
    );
  } catch {
    return false;
  }
}

/**
 * AuthRedirect handles the redirect from the stable auth domain (staging.rebuild.work)
 * to the original Vercel preview URL where the user initiated login.
 *
 * Flow:
 * 1. User on preview URL enters email
 * 2. Backend sends email with code
 * 3. User enters code → lands here
 * 4. This page redirects to preview-url/passwordless with Firebase params preserved
 * 5. Preview URL completes Firebase signInWithEmailLink
 */
export const AuthRedirect = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const redirectTo = searchParams.get("redirectTo");

    if (!redirectTo) {
      setError("Missing redirect destination");
      return;
    }

    if (!isValidPreviewUrl(redirectTo)) {
      setError("Invalid redirect destination");
      return;
    }

    // Preserve all Firebase auth params by appending them to the redirect URL
    const targetUrl = new URL(`${redirectTo}/passwordless`);

    // Copy all search params except redirectTo to the target URL
    searchParams.forEach((value, key) => {
      if (key !== "redirectTo") {
        targetUrl.searchParams.set(key, value);
      }
    });

    // Perform the redirect
    window.location.href = targetUrl.toString();
  }, [searchParams]);

  if (error) {
    return (
      <div className="bg-muted flex min-h-full flex-col items-center justify-center gap-6 p-6 md:p-10">
        <div className="flex w-full max-w-md flex-col gap-6">
          <a href="#" className="flex items-center gap-2 self-center font-medium">
            <img src="/logo-black.svg" alt="Rebuild" className="h-8" />
          </a>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Authentication Error</CardTitle>
              <CardDescription>
                {error}. Please try logging in again.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => navigate("/")}>
                Try again
              </Button>
            </CardContent>
          </Card>
          <div className="text-balance text-center text-xs text-muted-foreground">
            Need help?{" "}
            <a
              href="mailto:admin@rebuild.work"
              className="underline underline-offset-4 hover:text-primary"
            >
              Contact support
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <RebuildLoading />;
};
